import type { TargetingDefinition, TargetingTermValue } from '../../../models';
import type { TargetingValueOption } from '../../AdForm/adFormik';
import type { DropdownOption } from '../../Dropdown';
import { disableByDimensionId } from '../utils';

export function optionToTargetingValue<T extends TargetingDefinition>(
  selectedValue: DropdownOption & { description?: string | null }
): T {
  return {
    id: selectedValue.id ?? '',
    name: selectedValue.value,
    displayName: selectedValue.label,
    ...(selectedValue.description && { description: selectedValue.description }),
  } as T;
}

export const createDropdownOptionFromTargetingValue = ({
  value,
  dimension,
}: Pick<TargetingTermValue, 'value' | 'dimension'>): TargetingValueOption =>
  createTargetingValueDropdownOption(value, dimension.id);

export const createTargetingValueDropdownOption = (
  targetingValue: { id: string; name: string; displayName: string; description?: string | null },
  dimensionId: string
): TargetingValueOption => ({
  ...createTargetingValueDropdownOptionHelper(targetingValue, dimensionId),
  dimension: dimensionId,
});

export const createDropdownOptionForCategoryOrDimension = (
  category: Pick<TargetingDefinition, 'id' | 'displayName' | 'name'> & Partial<Pick<TargetingDefinition, 'description'>>
): DropdownOption => createTargetingValueDropdownOptionHelper(category, category.id);

const createTargetingValueDropdownOptionHelper = (
  {
    id,
    name,
    displayName,
    description = null,
  }: { id: string; name: string; displayName: string; description?: string | null },
  dimensionId: string
): TargetingValueOption => ({
  id,
  value: name,
  label: displayName,
  ...(description && {
    description,
    tooltip: {
      id: `${id}-tooltip`,
      message: description,
    },
  }),
  isDisabled: disableByDimensionId(dimensionId),
});

const PUBLISHER_ABBREVIATIONS: Record<string, string> = {
  DISNEY_PLUS: 'D+',
  HULU: 'Hulu',
  ESPN: 'ESPN',
};

export function composePublisherAbbreviation(publisher: string): string {
  return PUBLISHER_ABBREVIATIONS[publisher.toUpperCase()] ?? publisher;
}

const DIMENSIONS_FOR_PREPENDING_COUNTRY_AND_PUBLISHER = ['audience', 'internal', 'demo'];

export const prefixDisplayNameAsAppropriate = (
  rawData: TargetingDefinition[],
  dimensionGuid: string | undefined
): TargetingDefinition[] => {
  // prefix displayName with country and publisher info where applicable
  if (
    rawData &&
    dimensionGuid &&
    DIMENSIONS_FOR_PREPENDING_COUNTRY_AND_PUBLISHER.includes(dimensionGuid.toLowerCase())
  ) {
    return prefixDisplayNames(rawData, dimensionGuid);
  }
  return rawData;
};

export const prefixDisplayNames = (rawData: TargetingDefinition[], dimensionGuid: string): TargetingDefinition[] =>
  rawData.map((node) =>
    node.attributeMap
      ? { ...node, displayName: prefixDisplayNameAsAvailable(node.displayName, dimensionGuid, node.attributeMap) }
      : node
  );

const shouldPrefixDisplayName = (dimensionGuid: string | undefined): boolean =>
  !!dimensionGuid && DIMENSIONS_FOR_PREPENDING_COUNTRY_AND_PUBLISHER.includes(dimensionGuid.toLowerCase());

const composePrefix = (attributeMap: Record<string, unknown>): string => {
  const country = attributeMap?.country;
  const publisher =
    attributeMap?.publisher && typeof attributeMap.publisher === 'string'
      ? composePublisherAbbreviation(attributeMap.publisher)
      : null;
  return [country, publisher].filter(Boolean).join('_');
};

export const prefixDisplayNameAsAvailable = (
  displayNameRaw: string,
  dimensionGuid: string | undefined,
  attributeMap: Record<string, unknown>
): string => {
  if (!shouldPrefixDisplayName(dimensionGuid)) return displayNameRaw;
  const prefix = composePrefix(attributeMap);
  return prefix ? `${prefix}_${displayNameRaw}` : displayNameRaw;
};
